import React, { useEffect, useState } from 'react';
import api from '../../../utils/api';
import { useAuth } from '../../../context/AuthContext';
import { canManageCrmSalaries } from '../../../utils/crmPermissions';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Label } from '../../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { useToast } from '../../../hooks/use-toast';
import CrmSalaryStructureForm from './CrmSalaryStructureForm';

function empName(e) {
  return `${e.first_name || ''} ${e.last_name || ''}`.trim() || e.employee_code;
}

export default function CrmSalaryProfiles() {
  const { user } = useAuth();
  const isAdmin = canManageCrmSalaries(user?.role_slug);
  const { toast } = useToast();
  const [items, setItems] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const loadList = async () => {
    setLoading(true);
    try {
      const res = await api.get('/crm/payroll/structures');
      setItems(res.data?.data?.items || []);
    } catch (e) {
      toast({ title: 'Error', description: e.response?.data?.message, variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadList(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const openProfile = async (employeeId) => {
    setSelectedId(employeeId);
    try {
      const res = await api.get(`/crm/payroll/structures/${employeeId}`);
      setDetail(res.data?.data || null);
    } catch (e) {
      toast({ title: 'Error', description: e.response?.data?.message, variant: 'destructive' });
    }
  };

  const save = async (payload) => {
    if (!selectedId || !isAdmin) return;
    setSaving(true);
    try {
      await api.put(`/crm/payroll/structures/${selectedId}`, payload);
      toast({ title: 'Saved', description: 'Salary structure updated' });
      openProfile(selectedId);
      loadList();
    } catch (e) {
      toast({ title: 'Error', description: e.response?.data?.message, variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="text-muted-foreground">Loading profiles…</div>;

  return (
    <div className="grid gap-4 lg:grid-cols-3">
      <Card className="lg:col-span-1">
        <CardHeader><CardTitle className="text-base">Employees</CardTitle></CardHeader>
        <CardContent className="space-y-2 max-h-[70vh] overflow-y-auto">
          {items.length === 0 && <p className="text-sm text-muted-foreground">No structures yet</p>}
          {items.map((row) => (
            <button
              key={row.id}
              type="button"
              onClick={() => openProfile(row.employee_id)}
              className={`w-full text-left rounded-md border px-3 py-2 text-sm ${
                selectedId === row.employee_id ? 'border-primary bg-primary/5' : 'hover:bg-muted'
              }`}
            >
              <div className="font-medium">{empName(row)}</div>
              <div className="text-xs text-muted-foreground">
                {row.employee_code} · {row.department || 'No dept'} · Net ₹{Number(row.net_salary || 0).toLocaleString('en-IN')}
              </div>
            </button>
          ))}
          {isAdmin && (
            <EmployeePicker onPick={openProfile} />
          )}
        </CardContent>
      </Card>

      <div className="lg:col-span-2">
        {!detail ? (
          <Card><CardContent className="py-10 text-center text-muted-foreground">Select employee</CardContent></Card>
        ) : (
          <CrmSalaryStructureForm
            employee={detail.employee}
            structure={detail.structure}
            readOnly={!isAdmin}
            onSave={save}
          />
        )}
      </div>
    </div>
  );
}

function EmployeePicker({ onPick }) {
  const [employees, setEmployees] = useState([]);
  useEffect(() => {
    api.get('/crm/employees').then((r) => {
      setEmployees((r.data?.data?.items || []).filter((e) => e.status === 'active'));
    }).catch(() => {});
  }, []);
  return (
    <div className="pt-2 border-t">
      <Label className="text-xs">Open employee</Label>
      <Select onValueChange={(v) => onPick(Number(v))}>
        <SelectTrigger className="mt-1"><SelectValue placeholder="Choose…" /></SelectTrigger>
        <SelectContent>
          {employees.map((e) => (
            <SelectItem key={e.id} value={String(e.id)}>
              {empName(e)} ({e.employee_code})
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}
